import { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { useDIContext } from '@/Framework/DI/DIContext';
import CreditFlowResearchRepository
  from '@/finsight/infrastructure/repository/CreditFlowResearchRepository';
import { usePageFiltersContext } from '@/finsight/application/PageFilters/PageFiltersContext';
import useFetchCollection from '@/Framework/State/useFetchCollection';
import { ThemeType } from '@/finsight/domain/vo/CreditFlow/Articles/themeTypes';
import {
  IMarketArticle,
  IMarketArticlesResponse,
  IMarketArticlesTilePayload,
} from '@/finsight/domain/vo/CreditFlow/Articles/IMarketArticle';
import {
  createFilterConfig,
  FilterConfig,
  getFilterValues,
} from '@/finsight/application/CreditFlowResearch/Articles/ArticlesTile/helpers';

interface IArticleServerSideResponse extends IMarketArticlesResponse {
  payload: IMarketArticlesTilePayload,
  hasServerError?: boolean,
}

interface IUseArticleTitleProps {
  initialData: IArticleServerSideResponse,
  themeType: ThemeType,
  isSubscribeButtonVisible?: boolean,
}

const useArticlesTile = ({
  initialData: {
    payload: { search: initialSearch, ...payload },
    hasServerError,
    ...data
  },
  themeType,
  isSubscribeButtonVisible = true,
}: IUseArticleTitleProps) => {
  const { container } = useDIContext();
  const requestService = container.get<CreditFlowResearchRepository>(CreditFlowResearchRepository);
  const [isFirstFetching, setIsFirstFetching] = useState<boolean>(true);
  const [articlesPayload, setArticlesPayload] = useState<Omit<IMarketArticlesTilePayload, 'search'>>(payload);
  const [search, setSearch] = useState('');
  const { pageFilters: { productIds, regionIds, industry, sector } } = usePageFiltersContext();

  const fetch = !isFirstFetching || hasServerError
      ? requestService.getArticlesList : null;

  const articlesData = useFetchCollection<IMarketArticle, IMarketArticlesTilePayload>(
      fetch,
      articlesPayload as IMarketArticlesTilePayload,
      { collection: data.collection, totalCount: data.totalCount, perPage: articlesPayload.perPage },
  );

  const denyUpdateWithDealAndCompany = () => {
    return articlesPayload.filter?.includedDealIds?.length
        || articlesPayload.filter?.includedIssuerIds?.length
        || articlesPayload.filter?.includedParentIds?.length;
  };

  useEffect(() => () => {
    setIsFirstFetching(false);
  }, [
    articlesPayload,
    articlesData.page,
    articlesData.debouncedSearchString,
  ]);

  useEffect(() => articlesData.reset, []);

  useEffect(() => {
    if (
        (isEqual(articlesPayload.filter.includedProductIds, productIds)
      && isEqual(articlesPayload.filter.includedRegionIds, regionIds))
      || denyUpdateWithDealAndCompany()
    ) return;

    setArticlesPayload((prevState) => ({
      ...prevState,
      filter: {
        ...prevState.filter,
        includedProductIds: productIds,
        includedRegionIds: regionIds,
      } }));
  }, [productIds, regionIds]);

  const updateFilter = (config: FilterConfig) => {
    if (!config.entity) return;

    const { getFn, getCurrentValue, getOmitKeys } = getFilterValues(config);
    const newFilter = getFn(config.entity);
    const { filter: payloadFilter } = articlesPayload;
    const currentFilterValue = getCurrentValue(payloadFilter);

    if (isEqual(Object.values(newFilter)[0], currentFilterValue)) return;

    setArticlesPayload(({ filter, ...prevState }) => ({
      ...prevState,
      filter: {
        ...omit(filter, getOmitKeys()),
        ...newFilter,
      },
    }));
  };

  useEffect(() => {
    updateFilter(createFilterConfig('sector', sector));
  }, [sector]);

  useEffect(() => {
    updateFilter(createFilterConfig('industry', industry));
  }, [industry]);

  const onPageChange = (page: number) => setArticlesPayload({ ...articlesPayload, page });

  return {
    articlesData,
    themeType,
    search,
    setSearch,
    onPageChange,
    isSubscribeButtonVisible,
  };
};

type ArticlesTileContextType = ReturnType<typeof useArticlesTile>;

export const ArticlesTileContext = createContext<ArticlesTileContextType>(null);

export const useArticlesTileContext = () => {
  const context = useContext(ArticlesTileContext);
  if (!context) {
    throw new Error('useArticlesTileContext must be used within the ArticlesTileContext');
  }
  return context;
};

interface IArticlesTileContextProviderProps {
  children: ReactNode,
  initialData: IArticleServerSideResponse,
  themeType: ThemeType,
  isSubscribeButtonVisible?: boolean,
}

const ArticlesTileContextProvider = ({
  children,
  initialData,
  themeType,
  isSubscribeButtonVisible,
}: IArticlesTileContextProviderProps) => (
  <ArticlesTileContext.Provider value={ useArticlesTile({
    initialData,
    themeType,
    isSubscribeButtonVisible,
  }) }
  >
    { children }
  </ArticlesTileContext.Provider>
);

export default ArticlesTileContextProvider;
